import { Form, Modal, Row } from 'antd';
import React, { useContext, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import InputNumberComponent from 'components/atoms/InputNumberComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import { ERozsahPPN } from 'services/dokumenty/navyseni';
import { ETypPrevodu, IPozadavkyDLT, IPozadavkyPolozkaDLT } from 'services/specialniSluzby/uePozadavky';
import { IDataZalozeniPozadavek, IZalozeniPostId, ueZalozeniPozadavekDLT } from 'services/specialniSluzby/ueZalozeni';

import trueFalseSelect from 'data/codebooks/trueFalseSelect';

import dltUcetValidation from 'utils/validations/dltUcetValidation';
import dveDesetinneMistaValidation from 'utils/validations/dveDesetinneMistaValidation';
import numberValidation from 'utils/validations/numberValidation';

interface INovyPozadavekDLT {
    setZmenaPozadavku: (value: 'edit' | 'novy' | undefined) => void;
    setSelectedDetail: React.Dispatch<React.SetStateAction<IPozadavkyPolozkaDLT[] | undefined>>;
    zmenaPozadavku?: 'edit' | 'novy';
    data?: IPozadavkyDLT;
    detailData?: IPozadavkyPolozkaDLT;
}

const NovyPozadavekDLT: React.FC<INovyPozadavekDLT> = ({
    setZmenaPozadavku,
    data,
    zmenaPozadavku,
    detailData,
    setSelectedDetail
}) => {
    const { specialniSluzby } = useContext(Context);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState<boolean>(false);
    const [modalResp, setModalResp] = useState<string>('');
    const [, rerender] = useState<boolean>(false);

    return (
        <LoadingWrapper loading={loading}>
            <FormWrapper
                onFinish={vals => {
                    setLoading(true);
                    ueZalozeniPozadavekDLT(
                        data?.id || 0,
                        {
                            ucet: vals.ucet,
                            pocetcp: vals.pocetcp,
                            castka: vals.castka,
                            ...(vals.ppn && {
                                ppn: vals.ppn === 'true'
                            }),
                            ...(vals.cislozastavnismlouvy && {
                                cislozastavnismlouvy: vals.cislozastavnismlouvy
                            }),
                            ...(zmenaPozadavku === 'edit' && {
                                idpolozky: detailData?.idpolozky
                            })
                        } as IDataZalozeniPozadavek,
                        zmenaPozadavku === 'edit' ? 'PUT' : 'POST'
                    )
                        .then((resp: IZalozeniPostId) => {
                            if (resp.sankce) setModalResp('SANKCE');
                            else if (resp.stav === 'OK') {
                                setZmenaPozadavku(undefined);
                                setSelectedDetail(undefined);
                            } else setModalResp(resp.stav);
                        })
                        .catch((err: any) => console.log('Zalozeni POSTid err', err))
                        .finally(() => setLoading(false));
                }}
                outForm={form}
            >
                <InputComponent
                    name="ucet"
                    label="CISLO_UCTU"
                    maxLength={50}
                    rules={dltUcetValidation()}
                    initialValue={zmenaPozadavku === 'edit' ? detailData?.ucet : undefined}
                />
                <InputNumberComponent
                    name="pocetcp"
                    label="POCET_CP"
                    maxLength={12}
                    initialValue={zmenaPozadavku === 'edit' ? detailData?.pocetcp : undefined}
                    rules={numberValidation(true)}
                />
                {specialniSluzby?.detail?.typprevodu !== ETypPrevodu.BEZZAPLACENI && (
                    <InputNumberComponent
                        name="castka"
                        label="DVP_CELKOVA_CASTKA"
                        maxLength={12}
                        precision={2}
                        initialValue={zmenaPozadavku === 'edit' ? detailData?.castka : undefined}
                        rules={dveDesetinneMistaValidation(true)}
                    />
                )}
                {specialniSluzby?.detail?.ppn && (
                    <SelectComponent
                        isFormItem={true}
                        data={trueFalseSelect}
                        name="ppn"
                        defaultValue={
                            specialniSluzby.detail.rozsahppn === ERozsahPPN.VSECHNY
                                ? 'true'
                                : typeof detailData?.ppn === 'boolean'
                                  ? String(detailData.ppn)
                                  : undefined
                        }
                        labelParagraph={'PPN_SELECT'}
                        disabled={
                            !!form.getFieldValue('cislozastavnismlouvy') ||
                            (!!specialniSluzby?.pozadavek?.cislozastavnismlouvy &&
                                specialniSluzby?.pozadavek?.ppn === true) ||
                            specialniSluzby.detail.rozsahppn === ERozsahPPN.VSECHNY
                        }
                        onChange={val => {
                            val === 'true' &&
                                form.setFieldsValue({
                                    cislozastavnismlouvy: undefined
                                });
                            rerender(pv => !pv);
                        }}
                    />
                )}
                {(specialniSluzby?.detail?.ppn ? form.getFieldValue('ppn') === 'false' : true) && (
                    <InputComponent
                        name="cislozastavnismlouvy"
                        label="CISLO_ZASTAVNI_SMLOUVY_ROW"
                        initialValue={zmenaPozadavku === 'edit' ? detailData?.cislozastavnismlouvy : undefined}
                        maxLength={12}
                        rules={[
                            () => ({
                                validator(_, value: string) {
                                    if (!value) return Promise.resolve();
                                    if (/^6\d{11}$/.test(value)) return Promise.resolve();
                                    return Promise.reject(
                                        <TranslateText>SS_NOVY_RUCNI_VALIDACE_ZASTAVNI</TranslateText>
                                    );
                                }
                            })
                        ]}
                        notRequired={true}
                        onChange={() => rerender(pv => !pv)}
                        onBlur={() =>
                            form.getFieldValue('cislozastavnismlouvy') &&
                            form.setFieldsValue({
                                ppn: 'false'
                            })
                        }
                        disabled={
                            specialniSluzby?.detail?.rozsahppn === ERozsahPPN.VSECHNY ||
                            (!!specialniSluzby?.pozadavek?.cislozastavnismlouvy &&
                                specialniSluzby?.pozadavek?.ppn === true)
                        }
                    />
                )}

                <Row className="button-row">
                    <ButtonComponent
                        id="zpetBtn"
                        buttontext="ZPET"
                        type="default"
                        onClick={() => {
                            setZmenaPozadavku(undefined);
                        }}
                    />
                    <ButtonComponent id="pridatPozadavkeSouborBtn" buttontext="ZAPSAT" htmlType="submit" />
                </Row>
                <Modal
                    title={<TranslateText>SS_NOVY_RUCNI_MODAL_ZAPIS</TranslateText>}
                    open={modalResp !== ''}
                    onCancel={() => {
                        setModalResp('');
                    }}
                    footer={[
                        <Row>
                            <ButtonComponent
                                buttontext="ZAVRIT"
                                onClick={() => {
                                    setModalResp('');
                                }}
                                id="zavritModalBtn"
                            />
                        </Row>
                    ]}
                >
                    <Row>
                        <Paragraph
                            text={(modalResp === 'SANKCE' ? 'SS_NOVY_RUCNI_MODAL_RESP_' : 'ZALOZENI_') + modalResp}
                        />
                    </Row>
                </Modal>
            </FormWrapper>
        </LoadingWrapper>
    );
};

export default NovyPozadavekDLT;
